import { IAttribute } from 'app/model/base/annotation';
import { Dataset } from 'app/model/dataset/dataset';
import { Pipeline } from 'app/model/pipeline/pipeline';
import {
    failedExpressionResolution,
    IExpressionResolution,
    IExpressionResolver, IResolutionError,
    matchExpressionTypeToFieldType,
    ParsingErrorKind,
    ResolutionErrorKind
} from 'app/model/expressions/expression-resolution';
import {
    DataFactoryParameterResolver,
    DatasetParameterResolver,
    LinkedServiceParameterResolver,
    PipelineParameterResolver
} from 'app/model/expressions/parameter-resolvers';
import { PipelineExpressionResolver } from 'app/model/expressions/pipeline-expression-resolver';
import { FunctionResolver } from 'app/model/expressions/function-resolver';
import { ExpressionRegistry } from 'app/model/expressions/expression-registry';
import { ActivityOutputResolver } from 'app/model/expressions/bracket-expression-resolvers/activity-output-resolver';
import { ItemResolver } from 'app/model/expressions/item-resolver';
import { StringInterpolationResolver } from 'app/model/expressions/string-interpolation-resolver';
import { NoExpressionResolver } from 'app/model/expressions/no-expression-resolver';
import { Owned } from 'app/model/base/named';
import { SystemVariableResolver } from 'app/model/expressions/system-variable-resolver';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import { VariablesResolver } from 'app/model/expressions/bracket-expression-resolvers/variables-resolver';
import { GlobalVariables } from 'app/services/globals';
import { ModelService } from 'app/model/services/model.service';
import { LoggerService } from 'app/logger/logger.service';
import { Activity, ExpressionCategory } from 'shell-models-lib';
import { ExpressionResolverService, IResolutionObj } from 'app/services/expression-resolver-service';
import { serializeCircularReference } from 'app/shared/utils/string.util';
import type { IAzLgcAppLangHelperStatic } from 'app/services/azure-logic-app-lang/types';
import type { AzLgcExpDocument } from 'monaco-azure-logic-app-lang';
import { ExposureControlService } from '../exposure-control/base-exposure-control.service';
import { TriggerParameterResolver } from 'app/model/expressions/trigger-parameter-resolver';

// please first ensure this esmoudle is of the same lifecycle as the entry html then we could import the lazy chunk within it
//
// so far, this esmodule shared the same lifecycle as the app module( html ), thus we could import the lazy chunk
// over here for only once other than lazily loading it with cache check every time it ngInit
// eslint-disable-next-line @typescript-eslint/naming-convention
const AzLgcLangHelperDeferred = GlobalVariables.enableExpressionBuilderV2 ? import('app/services/azure-logic-app-lang/az-lgc-app-lang-helper-nodejs') : undefined;
let azLgcAppLangHelper: IAzLgcAppLangHelperStatic | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let potentialError: any | undefined;

export function initializeAzureLogicAppExpressionLanguage(){
    return new Promise<void>(async (resolve)=>{
        try{
            const azLgcLangHelperDeferred = await AzLgcLangHelperDeferred;
            const realAzLgcAppLangHelper = azLgcLangHelperDeferred.AzLgcAppLangHelper;
            await realAzLgcAppLangHelper.init;
            azLgcAppLangHelper = (realAzLgcAppLangHelper as unknown) as IAzLgcAppLangHelperStatic;
        }catch (err) {
            potentialError = err;
        }
        resolve();
    });
}

export class AzLgcAppExpLangNodejsService extends ExpressionResolverService {
    private static _registeredExpressions = Array.from(ExpressionRegistry.expressions);

    private static _getPipelineResolvers(entity: Owned, currentModelServiceInContext: ModelService): IExpressionResolver[] {
        const resolvers: IExpressionResolver[] = [];
        const resolversCallback: () => IExpressionResolver[] = () => resolvers;
        const callBackResolvers: IExpressionResolver[] = [];

        if (GlobalVariables.enableGlobalParameters) {
            callBackResolvers.push(new DataFactoryParameterResolver(entity, resolversCallback, currentModelServiceInContext.factory));
        }

        callBackResolvers.push(...[
            new DatasetParameterResolver(entity, resolversCallback),
            new ActivityOutputResolver(entity, resolversCallback),
            new PipelineExpressionResolver(entity, AzLgcAppExpLangNodejsService._registeredExpressions, resolversCallback),
            new ItemResolver(entity, resolversCallback),
            new FunctionResolver(entity, AzLgcAppExpLangNodejsService._registeredExpressions, resolversCallback),
            new VariablesResolver(entity, resolversCallback)
        ]);
        const noneCallBackResolvers = [
            new StringInterpolationResolver(entity, resolversCallback),
            new NoExpressionResolver()
        ];
        resolvers.push(...callBackResolvers);
        return [...callBackResolvers, ...noneCallBackResolvers];
    }

    private static _getDatasetResolvers(entity: Owned): IExpressionResolver[] {
        const resolvers: IExpressionResolver[] = [];
        const resolversCallback: () => IExpressionResolver[] = () => resolvers;
        const callBackResolvers = [
            new SystemVariableResolver(AzLgcAppExpLangNodejsService._registeredExpressions),
            new TriggerParameterResolver(AzLgcAppExpLangNodejsService._registeredExpressions),
            new DatasetParameterResolver(entity, resolversCallback),
            new PipelineParameterResolver(entity, resolversCallback),
            new FunctionResolver(entity, AzLgcAppExpLangNodejsService._registeredExpressions, resolversCallback),
            new VariablesResolver(entity, resolversCallback)
        ];
        const noneCallBackResolvers = [
            new StringInterpolationResolver(entity, resolversCallback),
            new NoExpressionResolver()
        ];
        resolvers.push(...callBackResolvers);
        return [...callBackResolvers, ...noneCallBackResolvers];
    }

    private static _getLinkedServiceResolvers(entity: Owned): IExpressionResolver[] {
        const resolvers: IExpressionResolver[] = [];
        const resolversCallback: () => IExpressionResolver[] = () => resolvers;
        const callBackResolvers = [
            new LinkedServiceParameterResolver(entity, resolversCallback),
            new PipelineParameterResolver(entity, resolversCallback),
            new FunctionResolver(entity, AzLgcAppExpLangNodejsService._registeredExpressions, resolversCallback)
        ];
        const noneCallBackResolvers = [
            new StringInterpolationResolver(entity, resolversCallback),
            new NoExpressionResolver()
        ];
        resolvers.push(...callBackResolvers);
        return [...callBackResolvers, ...noneCallBackResolvers];
    }

    private static _getResolution(childrenResolvers: IExpressionResolver[], expression: string, entity: Owned, field: IAttribute): IExpressionResolution {
        for (const resolver of childrenResolvers) {
            if (resolver.isOfType(expression)) {
                const applicability = resolver.checkApplicability(entity);
                if (applicability.isApplicable) {
                    const expressionResolution = resolver.resolve(expression);
                    if (expressionResolution.isValid) {
                        // ensure the expression type matches the field's type
                        if (matchExpressionTypeToFieldType(field, expressionResolution.returnType, entity)) {
                            return expressionResolution;
                        } else {
                            return failedExpressionResolution(expression, {
                                kind: ResolutionErrorKind.TypeMismatch,
                                details: {
                                    expressionType: expressionResolution.returnType,
                                    expressionSubType: expressionResolution.returnSubType,
                                    field
                                }
                            }, expressionResolution.expression, expressionResolution.dependencies, true);
                        }
                    } else {
                        return expressionResolution;
                    }
                } else {
                    return {
                        isValid: false,
                        source: expression,
                        error: applicability.error || { kind: ResolutionErrorKind.ExpressionNotApplicable },
                        treatAsWarning: applicability.treatAsWarning
                    };
                }
            }
        }
        return undefined;
    }

    private static _getChildrenResolvers(entity: Owned, currentModelServiceInContext: ModelService): IExpressionResolver[] {
        const childrenResolvers: IExpressionResolver[] = [];
        if (!entity || !entity.concept) {
            return childrenResolvers;
        }
        if (entity.concept instanceof Pipeline) {
            childrenResolvers.push(...AzLgcAppExpLangNodejsService._getPipelineResolvers(entity, currentModelServiceInContext));
        } else if (entity.concept instanceof Dataset) {
            childrenResolvers.push(...AzLgcAppExpLangNodejsService._getDatasetResolvers(entity));
        } else if (entity.concept instanceof LinkedService) {
            childrenResolvers.push(...AzLgcAppExpLangNodejsService._getLinkedServiceResolvers(entity));
        }
        return childrenResolvers;
    }

    private readonly isMicrosoftInternal: boolean = false;

    constructor(
        private readonly _logger: LoggerService,
        private readonly _modelService: ModelService,
        private readonly _ecService: ExposureControlService
    ) {
        super();
        this.isMicrosoftInternal = GlobalVariables.isMicrosoftInternal;
        if (azLgcAppLangHelper){
            azLgcAppLangHelper.globalTraceHandler = (namespace, properties)=>{
                this._logger.log(
                    `AzLgcAppExpLangNodejsService.nextGenExpBldrTrace`,
                    namespace,
                    properties
                );
            };
            azLgcAppLangHelper.globalErrorHandler = (namespace, err)=>{
                this._logger.error(
                    `AzLgcAppExpLangNodejsService.nextGenExpBldrErr`,
                    namespace,
                    {
                        name: err.name,
                        message: err.message,
                        stack: err.stack || undefined
                    }
                );
            };
        }
    }

    public buildParserV2FromOwnedAndAttribute(owned: Owned, _field: IAttribute) {
        if (!azLgcAppLangHelper){
            this._logger.error(
                'AzLgcAppExpLangNodejsService.buildParserV2FromOwnedAndAttribute',
                'featureFlagDisabledParserV2Builder',
                potentialError
            );
            return undefined;
        }
        try{
            this._logger.log(
                'AzLgcAppExpLangNodejsService.buildParserV2FromOwnedAndAttribute',
                'start parsing',
                potentialError
            );
            let theActivity: Activity | undefined;
            if (owned instanceof Activity){
                theActivity = owned;
            }
            const theEntity = this._findEntity(owned);
            const showTriggerParameters = this._ecService.enableFabricStorageTrigger;
            const theSymbolTable = azLgcAppLangHelper.staticGenerateRootSymbolTableSync(
                theEntity,
                theActivity,
                this._modelService,
                false,
                showTriggerParameters
            );
            return {
                parse: (text: string)=>{
                    return azLgcAppLangHelper.staticManuallyParseSync(theSymbolTable, text);
                }
            };
        }catch (err) {
            this._logger.error(
                'AzLgcAppExpLangNodejsService.buildParserV2FromOwnedAndAttribute',
                'FailedToInitialized',
                err
            );
        }
        return undefined;
    }

    /**
     * We gonna gradually deprecate this method and migrate to the next gen builder: buildParserV2FromOwnedAndAttribute
     * @deprecated
     * @param entity
     * @param field
     */
    public given(entity: Owned, field: IAttribute): IResolutionObj {
        let parserV2 = this.isMicrosoftInternal?
            this.buildParserV2FromOwnedAndAttribute(entity, field) : undefined;
        return {
            /**
             * We gonna gradually deprecate this method and migrate to the next gen parser method: parse
             * @deprecated
             * @param expression
             */
            resolve: (expression: string) => {
                if (!expression) {
                    return failedExpressionResolution(expression, { kind: ResolutionErrorKind.EmptyExpression });
                }
                if (expression.startsWith(' ')) {
                    // Treat as warning.
                    return failedExpressionResolution(
                        expression,
                        { kind: ResolutionErrorKind.ExpressionContainsWhiteSpace },
                        undefined,
                        undefined,
                        true);
                }
                const childrenResolvers = AzLgcAppExpLangNodejsService._getChildrenResolvers(entity, this._modelService);
                if (childrenResolvers.length === 0) {
                    return {
                        isValid: false,
                        source: expression,
                        error: { kind: ResolutionErrorKind.UnsupportedConcept }
                    };
                }
                const resolution = AzLgcAppExpLangNodejsService._getResolution(childrenResolvers, expression, entity, field);
                const failedResolution = {
                    isValid: false,
                    source: expression,
                    error: { kind: ResolutionErrorKind.Unrecognized, details: expression }
                };
                let res = resolution || failedResolution;
                // eslint-disable-next-line no-console

                let res2: AzLgcExpDocument|undefined = parserV2? parserV2.parse(expression): undefined;

                // log brief info for a mismatched case
                if (this.isMicrosoftInternal && res && res2){
                    const boolRes1 = res.isValid;
                    const boolRes2 = res2.validateResult?.errors?.length === 0;
                    if (boolRes1 !== boolRes2){
                        this._logger.log(
                            'AzLgcAppExpLangNodejsService.given.resolve',
                            'Gen1&2ParserResMisMatchedV3',
                            {
                                v1: {
                                    isValid: boolRes1,
                                    error: serializeCircularReference(res.error)
                                },
                                v2: {
                                    isValid: boolRes2,
                                    exp: serializeCircularReference(expression),
                                    problems: serializeCircularReference(
                                        [res2.validateResult?.errors ?? []].map((oneError)=>({
                                            ...oneError,
                                            // do not log the complete ast node since it is not helping anymore
                                            node: undefined
                                        }))
                                    )
                                }
                            }
                        );
                    }
                }
                // hijack ad-hoc cases referring v2 result if needed
                if (
                    !res.isValid &&
                    (
                        (
                            // MissingEnclosingParenthesis or MissingDot might be false alarm
                            res.error?.kind === ResolutionErrorKind.InvalidSyntax &&
                            (
                                (res.error as IResolutionError)?.parsingError?.error === ParsingErrorKind.MissingEnclosingParanthesis ||
                                (res.error as IResolutionError)?.parsingError?.error === ParsingErrorKind.MissingDot
                            )
                        )||
                        (
                            // Excess'sNestedProperties might be false alarm
                            res.error?.kind === ResolutionErrorKind.ExcesssNestedProperties
                        )
                    )
                ){
                    parserV2 = parserV2 || this.buildParserV2FromOwnedAndAttribute(entity, field);
                    res2 = res2 || parserV2.parse(expression);
                    if (!res2.validateResult?.errors?.length){
                        // no error form v2, thus regard it as a valid expression
                        res = {
                            isValid: true,
                            source: expression,
                        };
                    }
                }
                return res;
            },
            getExpressionCategory: (expression: string) => {
                return ExpressionCategory.None;
            },

            getExpressionShorthand: (expression: string) => {
                return '';
            }
        };
    }

    private _findEntity(owned: Owned): Pipeline | Dataset | LinkedService | undefined{
        if (!owned || !owned.concept) {
            return undefined;
        }
        const theConcept = owned.concept;
        if (
            theConcept instanceof Pipeline ||
            theConcept instanceof Dataset ||
            theConcept instanceof LinkedService
        ) {
            return theConcept;
        }
        return undefined;
    }
}
